/**
 * API Proxy Module
 * Routes frontend API calls through the secure Lambda proxy
 */

import axios from 'axios';

// Proxy URL - update this with your actual Lambda URL
const PROXY_BASE_URL = 'https://devapi-frankfurt.rezcomm.com/sbook';
const ORDERREF_PROXY_BASE_URL = 'https://devapi-frankfurt.rezcomm.com/sbook/ordr';

/**
 * Get or create session token
 */
async function getSessionToken(brandId, deviceType, deviceOs, searchIp) {
  const existingToken = sessionStorage.getItem('proxy_session_token');
  if (existingToken) {
    return existingToken;
  }

  const response = await axios.post(`${PROXY_BASE_URL}/session/create`, {
    brand_id: brandId,
    device_type: deviceType,
    device_os: deviceOs,
    search_ip: searchIp
  });

  const token = response.data.session_token;
  sessionStorage.setItem('proxy_session_token', token);
  return token;
}

/**
 * Claim or refresh a stable cart-level order reference
 */
export async function claimOrderReference(payload) {
  const response = await axios.post(`${ORDERREF_PROXY_BASE_URL}/orderref/claim`, payload, {
    headers: {
      'Content-Type': 'application/json'
    }
  });

  return response.data;
}

/**
 * CPK Search - proxied through Lambda
 */
export async function searchCarpark(searchParams) {
  const sessionToken = await getSessionToken(
    searchParams.brand_id,
    searchParams.device_type,
    searchParams.device_os,
    searchParams.search_ip
  );

  const response = await axios.post(`${PROXY_BASE_URL}/parking/search`, searchParams, {
    headers: {
      'x-session-id': sessionToken,
      'Content-Type': 'application/json'
    }
  });

  return response.data;
}

/**
 * Seasonal Parking Search - proxied through Lambda
 */
export async function searchSeasonalParking(searchParams) {
  const sessionToken = await getSessionToken(
    searchParams.brand_id,
    searchParams.device_type,
    searchParams.device_os,
    searchParams.search_ip
  );

  const response = await axios.post(`${PROXY_BASE_URL}/parking/season/search`, searchParams, {
    headers: {
      'x-session-id': sessionToken,
      'Content-Type': 'application/json'
    }
  });

  return response.data;
}

/**
 * Lounge Search - proxied through Lambda
 */
export async function searchLounge(searchParams) {
  const sessionToken = await getSessionToken(
    searchParams.brand_id,
    searchParams.device_type,
    searchParams.device_os,
    searchParams.search_ip
  );

  const response = await axios.post(`${PROXY_BASE_URL}/lounge/search`, searchParams, {
    headers: {
      'x-session-id': sessionToken,
      'Content-Type': 'application/json'
    }
  });

  return response.data;
}

/**
 * Lounge Reserve - proxied through Lambda
 */
export async function reserveLounge(payload) {
  const sessionToken = await getSessionToken(
    payload.brand_id,
    payload.device_type,
    payload.device_os,
    payload.search_ip
  );

  const response = await axios.post(`${PROXY_BASE_URL}/lounge/reserve`, payload, {
    headers: {
      'x-session-id': sessionToken,
      'Content-Type': 'application/json'
    }
  });

  return response.data;
}

/**
 * Fasttrack Search - proxied through Lambda
 */
export async function searchFasttrack(searchParams) {
  const sessionToken = await getSessionToken(
    searchParams.brand_id,
    searchParams.device_type,
    searchParams.device_os,
    searchParams.search_ip
  );

  const response = await axios.post(`${PROXY_BASE_URL}/fasttrack/search`, searchParams, {
    headers: {
      'x-session-id': sessionToken,
      'Content-Type': 'application/json'
    }
  });

  return response.data;
}

/**
 * Fasttrack Reserve - proxied through Lambda
 */
export async function reserveFasttrack(payload) {
  const sessionToken = await getSessionToken(
    payload.brand_id,
    payload.device_type,
    payload.device_os,
    payload.search_ip
  );

  const response = await axios.post(`${PROXY_BASE_URL}/fasttrack/reserve`, payload, {
    headers: {
      'x-session-id': sessionToken,
      'Content-Type': 'application/json'
    }
  });

  return response.data;
}

/**
 * Coach Search - proxied through Lambda
 */
export async function searchCoach(searchParams) {
  const sessionToken = await getSessionToken(
    searchParams.brand_id,
    searchParams.device_type,
    searchParams.device_os,
    searchParams.search_ip
  );

  const response = await axios.post(`${PROXY_BASE_URL}/coach/search`, searchParams, {
    headers: {
      'x-session-id': sessionToken,
      'Content-Type': 'application/json'
    }
  });

  return response.data;
}

/**
 * Coach Reserve - proxied through Lambda
 */
export async function reserveCoach(payload) {
  const sessionToken = await getSessionToken(
    payload.brand_id,
    payload.device_type,
    payload.device_os,
    payload.search_ip
  );

  const response = await axios.post(`${PROXY_BASE_URL}/coach/reserve`, payload, {
    headers: {
      'x-session-id': sessionToken,
      'Content-Type': 'application/json'
    }
  });

  return response.data;
}

/**
 * Airport Tours Search - proxied through Lambda
 */
export async function searchAtr(searchParams) {
  const sessionToken = await getSessionToken(
    searchParams.brand_id,
    searchParams.device_type,
    searchParams.device_os,
    searchParams.search_ip
  );

  const response = await axios.post(`${PROXY_BASE_URL}/atr/search`, searchParams, {
    headers: {
      'x-session-id': sessionToken,
      'Content-Type': 'application/json'
    }
  });

  return response.data;
}

/**
 * Airport Tours Reserve - proxied through Lambda
 */
export async function reserveAtr(payload) {
  const sessionToken = await getSessionToken(
    payload.brand_id,
    payload.device_type,
    payload.device_os,
    payload.search_ip
  );

  const response = await axios.post(`${PROXY_BASE_URL}/atr/reserve`, payload, {
    headers: {
      'x-session-id': sessionToken,
      'Content-Type': 'application/json'
    }
  });

  return response.data;
}

/**
 * CPK Reserve - proxied through Lambda
 */
export async function reserveCarpark(payload) {
  const sessionToken = await getSessionToken(
    payload.brand_id,
    payload.device_type,
    payload.device_os,
    payload.search_ip
  );

  const response = await axios.post(`${PROXY_BASE_URL}/parking/reserve`, payload, {
    headers: {
      'x-session-id': sessionToken,
      'Content-Type': 'application/json'
    }
  });

  return response.data;
}

/**
 * Upsell availability - proxied through Lambda
 */
export async function upsellAvailability(payload) {
  const sessionToken = sessionStorage.getItem('proxy_session_token');
  const response = await axios.post(`${PROXY_BASE_URL}/upsell/availability`, payload, {
    headers: {
      'x-session-id': sessionToken,
      'Content-Type': 'application/json'
    }
  });
  return response.data;
}

/**
 * Cross-sell availability - proxied through Lambda
 */
export async function crossSellAvailability(payload) {
  const sessionToken = sessionStorage.getItem('proxy_session_token');
  const response = await axios.post(`${PROXY_BASE_URL}/crosssell/availability`, payload, {
    headers: {
      'x-session-id': sessionToken,
      'Content-Type': 'application/json'
    }
  });
  return response.data;
}

/**
 * Add item to cart
 */
export async function addCartItem(quoteId, quantity, paxDetails) {
  const sessionToken = sessionStorage.getItem('proxy_session_token');

  const response = await axios.post(`${PROXY_BASE_URL}/cart/items`, {
    quote_id: quoteId,
    quantity,
    pax_details: paxDetails
  }, {
    headers: {
      'x-session-id': sessionToken,
      'Content-Type': 'application/json'
    }
  });

  return response.data;
}

/**
 * Upsert a raw cart item (used for ADHOC/session parity)
 */
export async function upsertCartItem(itemPayload) {
  const sessionToken = sessionStorage.getItem('proxy_session_token');
  if (!sessionToken) {
    return null;
  }

  const response = await axios.patch(`${PROXY_BASE_URL}/cart/items`, itemPayload, {
    headers: {
      'x-session-id': sessionToken,
      'Content-Type': 'application/json'
    }
  });

  return response.data;
}

/**
 * Delete one cart item
 */
export async function deleteCartItem(itemPayload, sessionTokenOverride = null) {
  const sessionToken = sessionTokenOverride || sessionStorage.getItem('proxy_session_token');
  if (!sessionToken) {
    return null;
  }

  const response = await axios.delete(`${PROXY_BASE_URL}/cart/items`, {
    headers: {
      'x-session-id': sessionToken,
      'Content-Type': 'application/json'
    },
    data: itemPayload
  });

  return response.data;
}

/**
 * Get cart
 */
export async function getCart(sessionTokenOverride = null) {
  const sessionToken = sessionTokenOverride || sessionStorage.getItem('proxy_session_token');

  const response = await axios.get(`${PROXY_BASE_URL}/cart`, {
    headers: {
      'x-session-id': sessionToken
    }
  });

  return response.data;
}

/**
 * Get cart summary
 */
export async function getCartSummary(sessionTokenOverride = null) {
  const sessionToken = sessionTokenOverride || sessionStorage.getItem('proxy_session_token');

  const response = await axios.post(`${PROXY_BASE_URL}/cart/summary`, {}, {
    headers: {
      'x-session-id': sessionToken,
      'Content-Type': 'application/json'
    }
  });

  return response.data;
}

/**
 * Clear cart
 */
export async function clearCart(sessionTokenOverride = null) {
  const sessionToken = sessionTokenOverride || sessionStorage.getItem('proxy_session_token');

  const response = await axios.delete(`${PROXY_BASE_URL}/cart`, {
    headers: {
      'x-session-id': sessionToken
    }
  });

  return response.data;
}

/**
 * Apply promo code
 */
export async function applyPromo(payload) {
  const sessionToken = sessionStorage.getItem('proxy_session_token');
  const requestBody = typeof payload === 'string' ? { promo_code: payload } : payload;

  const response = await axios.post(`${PROXY_BASE_URL}/promo/apply`, requestBody, {
    headers: {
      'x-session-id': sessionToken,
      'Content-Type': 'application/json'
    }
  });

  return response.data;
}

/**
 * Remove promo code
 */
export async function removePromo(sessionTokenOverride = null) {
  const sessionToken = sessionTokenOverride || sessionStorage.getItem('proxy_session_token');

  const response = await axios.delete(`${PROXY_BASE_URL}/promo`, {
    headers: {
      'x-session-id': sessionToken
    }
  });

  return response.data;
}

/**
 * Cancel promo code
 */
export async function cancelPromo(payload) {
  const sessionToken = sessionStorage.getItem('proxy_session_token');

  const response = await axios.post(`${PROXY_BASE_URL}/promo/cancel`, payload, {
    headers: {
      'x-session-id': sessionToken,
      'Content-Type': 'application/json'
    }
  });

  return response.data;
}

/**
 * Create checkout session
 */
export async function createCheckoutSession() {
  const sessionToken = sessionStorage.getItem('proxy_session_token');

  const response = await axios.post(`${PROXY_BASE_URL}/checkout/create-session`, {}, {
    headers: {
      'x-session-id': sessionToken,
      'Content-Type': 'application/json'
    }
  });

  return response.data;
}
